import React, { useMemo, useState } from "react";
import { BatchStatus, TimelineBatch } from "../../../types/mes";
import { filterActiveBatches, getBatchKey, groupBatchesByMixer } from "./planningUtils";

interface Props {
  batches: Partial<TimelineBatch>[];
  /** Apply a new order (active batches only) together with the mandatory reason. */
  onApply: (
    batchOrder: { batch_id: number; redoslijed: number }[],
    reason: string
  ) => void;
  disabled?: boolean;
  formatTime: (dateStr?: string) => string;
}

const STATUS_LABELS: Record<BatchStatus, string> = {
  planned: "Planirano",
  ready: "Spremno",
  in_progress: "U tijeku",
  completed: "Zavrseno",
  on_hold: "Pauzirano",
  skipped: "Preskoceno",
  canceled: "Otkazano",
};

/**
 * Operativna izmjena redoslijeda (mes-pekara "CONFIRMED MODE"): reorder only the
 * still-active batches (planned/ready/on_hold) of a confirmed timeline. Finished
 * or running batches stay frozen and are not shown here. A reason is mandatory;
 * the backend recomputes times for the active batches and logs each move as a
 * dispatch override.
 */
const OperationalReorderPanel: React.FC<Props> = ({ batches, onApply, disabled, formatTime }) => {
  const activeBatches = useMemo(() => filterActiveBatches(batches), [batches]);
  const groups = useMemo(() => groupBatchesByMixer(activeBatches), [activeBatches]);

  // Batches this panel deliberately hides (already running or finished). Naming
  // them matters: a planner who sees 4 rows for a 5-batch day has no way to tell
  // whether the fifth is frozen or missing.
  const hiddenBatches = useMemo(() => {
    const active = new Set(activeBatches);
    return batches.filter((b) => !active.has(b));
  }, [batches, activeBatches]);

  const hiddenSummary = useMemo(() => {
    const counts = new Map<BatchStatus, number>();
    hiddenBatches.forEach((b) => {
      const status = (b.BatchStatus as BatchStatus) || "planned";
      counts.set(status, (counts.get(status) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([status, count]) => `${count} ${STATUS_LABELS[status].toLowerCase()}`)
      .join(", ");
  }, [hiddenBatches]);

  const [orders, setOrders] = useState<Record<number, number>>({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Batches picked (by key) to bulk-send to the end of their mixer ("NOĆ").
  const [nightPicks, setNightPicks] = useState<Set<number>>(new Set());

  const getOrder = (batch: Partial<TimelineBatch>, index: number): number => {
    const key = getBatchKey(batch, index);
    return orders[key] ?? batch.Sequence ?? index + 1;
  };

  const setOrder = (batch: Partial<TimelineBatch>, index: number, value: number) => {
    const key = getBatchKey(batch, index);
    setOrders((prev) => ({ ...prev, [key]: value }));
  };

  const toggleNight = (batch: Partial<TimelineBatch>, index: number) => {
    const key = getBatchKey(batch, index);
    setNightPicks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // NOĆ: push the picked batches of a mixer to the END of that mixer's order in one
  // action (no per-batch drag/drop). Unpicked batches keep their relative order at the
  // front; picked ones follow, also keeping their relative order. Renumbers redoslijed
  // 1..N within the mixer so the change previews immediately via getOrder().
  const sendPickedToNight = (mixerBatches: Partial<TimelineBatch>[]) => {
    const withIdx = mixerBatches.map((b) => ({ b, i: activeBatches.indexOf(b) }));
    const isPicked = ({ b, i }: { b: Partial<TimelineBatch>; i: number }) =>
      nightPicks.has(getBatchKey(b, i));
    const front = withIdx.filter((x) => !isPicked(x)).sort((a, c) => getOrder(a.b, a.i) - getOrder(c.b, c.i));
    const night = withIdx.filter(isPicked).sort((a, c) => getOrder(a.b, a.i) - getOrder(c.b, c.i));
    if (night.length === 0) {
      setError("Odaberi barem jedan zamjes za slanje u NOĆ.");
      return;
    }
    setError(null);
    const newOrder = [...front, ...night];
    setOrders((prev) => {
      const next = { ...prev };
      newOrder.forEach(({ b, i }, pos) => { next[getBatchKey(b, i)] = pos + 1; });
      return next;
    });
    // Clear picks only for this mixer's batches.
    setNightPicks((prev) => {
      const next = new Set(prev);
      withIdx.forEach(({ b, i }) => next.delete(getBatchKey(b, i)));
      return next;
    });
  };

  const handleApply = () => {
    if (!reason.trim()) {
      setError("Upisi razlog intervencije prije primjene.");
      return;
    }
    setError(null);

    // Build the desired order per mixer (sorted by the edited redoslijed),
    // then flatten — only active batches carry a real DB id.
    const ordered: { batch_id: number; redoslijed: number }[] = [];
    Object.values(groups).forEach((mixerBatches) => {
      [...mixerBatches]
        .sort((a, b) => {
          const ia = activeBatches.indexOf(a);
          const ib = activeBatches.indexOf(b);
          return getOrder(a, ia) - getOrder(b, ib);
        })
        .forEach((batch) => {
          if (batch.ID) ordered.push({ batch_id: batch.ID, redoslijed: ordered.length + 1 });
        });
    });

    if (ordered.length === 0) {
      setError("Nema aktivnih zamjesa za izmjenu.");
      return;
    }

    onApply(ordered, reason.trim());
    setOrders({});
    setReason("");
  };

  const hiddenNotice = hiddenBatches.length > 0 && (
    <p className="op-hidden-note">
      Prikazano {activeBatches.length} od {batches.length} zamjesa — {hiddenBatches.length}{" "}
      {hiddenBatches.length === 1 ? "je zamrznut" : "su zamrznuti"} ({hiddenSummary}) i ne moze im se
      mijenjati redoslijed:{" "}
      {hiddenBatches.map((b, i) => (
        <React.Fragment key={b.ID ?? b.MixCode ?? i}>
          {i > 0 && ", "}
          <code className="batch-id-code">{b.MixCode}</code>
        </React.Fragment>
      ))}
    </p>
  );

  if (activeBatches.length === 0) {
    return (
      <div className="mes-section operational-reorder">
        <h3 style={{ marginTop: 0 }}>Operativna izmjena redoslijeda</h3>
        <p style={{ color: "#95a5a6", margin: 0 }}>Nema aktivnih zamjesa za izmjenu.</p>
        {hiddenNotice}
      </div>
    );
  }

  return (
    <div className="mes-section operational-reorder">
      <div className="op-head">
        <div>
          <h3 style={{ margin: 0 }}>Operativna izmjena redoslijeda</h3>
          <p className="editor-hint" style={{ margin: "4px 0 0" }}>
            Promijeni redoslijed preostalih (aktivnih) zamjesa u hodu. Zavrsene i
            zamjese u tijeku ostaju nepromijenjene. Potvrdeni plan ostaje sacuvan
            u bazi — svaka promjena se biljezi s razlogom.
          </p>
        </div>
      </div>

      {hiddenNotice}

      {Object.entries(groups).map(([mixer, mixerBatches]) => (
        <div key={mixer} className="op-mixer-block">
          <h4>
            🥣 {mixer} <span className="count-badge">{mixerBatches.length} zamjesa</span>
            <button
              type="button"
              className="btn-edit op-night-btn"
              onClick={() => sendPickedToNight(mixerBatches)}
              disabled={disabled}
              title="Pošalji označene zamjese na kraj rasporeda ovog miksera (NOĆ)"
            >
              🌙 Pošalji u NOĆ
            </button>
          </h4>
          <div style={{ overflowX: "auto" }}>
            <table className="mes-table timeline-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>NOĆ</th>
                  <th>Novi redoslijed</th>
                  <th>ID zamjesa</th>
                  <th>Proizvod</th>
                  <th>Start mij.</th>
                  <th>Kraj pec.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {mixerBatches.map((batch) => {
                  const index = activeBatches.indexOf(batch);
                  return (
                    <tr key={batch.MixCode || index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={nightPicks.has(getBatchKey(batch, index))}
                          onChange={() => toggleNight(batch, index)}
                          disabled={disabled}
                          title="Označi za slanje u NOĆ"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          className="input-classic input-sm"
                          value={getOrder(batch, index)}
                          onChange={(e) => setOrder(batch, index, Number(e.target.value))}
                          disabled={disabled}
                        />
                      </td>
                      <td><code className="batch-id-code">{batch.MixCode}</code></td>
                      <td className="batch-product">{batch.ProductName}</td>
                      <td>{formatTime(batch.MixingStart)}</td>
                      <td>{formatTime(batch.BakingEnd)}</td>
                      <td>
                        <span className={`status-pill status-${batch.BatchStatus || "planned"}`}>
                          <span className="status-dot"></span>
                          {STATUS_LABELS[(batch.BatchStatus as BatchStatus) || "planned"]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="op-apply-row">
        <input
          className="input-classic"
          placeholder="Razlog intervencije (obavezno)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={disabled}
        />
        <button
          type="button"
          className="btn-primary"
          onClick={handleApply}
          disabled={disabled}
        >
          Primijeni operativnu izmjenu
        </button>
      </div>
      {error && <p className="op-error">{error}</p>}

      <style>{`
        .operational-reorder .op-mixer-block { margin-top: 16px; }
        .operational-reorder .op-mixer-block h4 {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 8px;
          color: #ecf0f1;
        }
        .operational-reorder .count-badge {
          font-size: 12px;
          font-weight: 500;
          color: #95a5a6;
          background: #34495e;
          border-radius: 10px;
          padding: 2px 10px;
        }
        .operational-reorder .input-sm { width: 90px; }
        .operational-reorder .op-night-btn { margin-left: auto; font-size: 12px; padding: 4px 10px; }
        .operational-reorder .op-apply-row {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-top: 16px;
        }
        .operational-reorder .op-apply-row .input-classic { flex: 1; max-width: 480px; }
        .operational-reorder .op-error { color: #e74c3c; margin: 8px 0 0; }
        .operational-reorder .op-hidden-note {
          margin: 12px 0 0;
          padding: 8px 12px;
          border-left: 3px solid #f39c12;
          background: rgba(243, 156, 18, 0.1);
          color: #bdc3c7;
          font-size: 13px;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
};

export default OperationalReorderPanel;
